import { useMutation } from '@apollo/client/react';
import { CardPropertyDetailsFragmentDoc } from '@graphql/fragments/cards/property.graphql';
import {
  CreateCardPropertyDocument,
  RemoveCardPropertyDocument,
  UpdateCardPropertyDocument,
} from '@graphql/mutations/cards/property.graphql';
import { cardClaimContext } from 'app-next/claim/card/contexts';
import { useContext } from 'react';

import type { CardAttributeType, CreateCardPropertyInput, UpdateCardPropertyInput } from '.cache/__types__';

export const useCardPropertyMutations = (
  cardId: string,
): {
  create: (input: CreateCardPropertyInput, attributeType: CardAttributeType) => void;
  update: (input: UpdateCardPropertyInput, attributeType: CardAttributeType) => void;
  remove: (cardId: string, propertyId: string, propertyName: string | undefined) => void;
} => {
  const [createCardPropertyMutation] = useMutation(CreateCardPropertyDocument);
  const [updateCardPropertyMutation] = useMutation(UpdateCardPropertyDocument);
  const [removeCardPropertyMutation] = useMutation(RemoveCardPropertyDocument);

  const { claimCardToken } = useContext(cardClaimContext);

  /* 
    If we're running mutations during the card claim flow,
    then we need to override the authorization headers to use the
    Claim Card Token.
  */
  const mutationContext = claimCardToken
    ? {
        headers: {
          authorization: `ClaimCard ${claimCardToken}`,
        },
      }
    : undefined;

  function handleCreate(input: CreateCardPropertyInput, attributeType: CardAttributeType) {
    createCardPropertyMutation({
      context: mutationContext,
      variables: {
        input,
      },
      optimisticResponse: {
        createCardProperty: {
          __typename: 'CreateCardPropertyPayload',
          property: {
            __typename: 'CardProperty',
            id: 'temp-id',
            attribute_type: attributeType,
            stage: null,
            template: null,
            template_id: null,
            ...input,
          },
        },
      },
      update: (cache, { data }) => {
        const newField = data?.createCardProperty;
        if (!newField || !cardId) return;

        const propertyName = data?.createCardProperty?.property.attribute_type?.name;
        if (!propertyName) return;

        const newCardPropertyRef = cache.writeFragment({
          // We must prefix this id with the string 'CardField:' otherwise the resulting cache ref
          // won't actually point to the right place
          id: `CardProperty:${data.createCardProperty?.property.id}`,
          data: data.createCardProperty?.property,
          fragment: CardPropertyDetailsFragmentDoc,
          fragmentName: 'cardPropertyDetails',
        });

        cache.modify({
          id: cache.identify({ __typename: 'Card', id: cardId }),
          fields: {
            properties(existingCardPropertyRefs) {
              return {
                ...existingCardPropertyRefs,
                [propertyName]: newCardPropertyRef,
              };
            },
          },
        });
      },
    });
  }

  function handleUpdate(input: UpdateCardPropertyInput, attributeType: CardAttributeType) {
    updateCardPropertyMutation({
      context: mutationContext,
      variables: {
        input,
      },
      optimisticResponse: {
        updateCardProperty: {
          __typename: 'UpdateCardPropertyPayload',
          property: {
            __typename: 'CardProperty',
            // Have to set these to null in case they're not provided by the input
            attribute_type: attributeType,
            template: null,
            ...input,
          },
        },
      },
    });
  }

  function handleRemove(cardId: string, propertyId: string, propertyName: string | undefined) {
    removeCardPropertyMutation({
      context: mutationContext,
      variables: {
        input: {
          id: propertyId,
          card_id: cardId,
        },
      },
      optimisticResponse: {
        removeCardProperty: {
          id: propertyId,
        },
      },
      update: (cache) => {
        if (!propertyName) {
          return;
        }

        cache.modify({
          id: cache.identify({ __typename: 'Card', id: cardId }),
          fields: {
            // Remove the deleted field from the cache via the card object.
            properties(existingCardProperties) {
              return {
                ...existingCardProperties,
                [propertyName]: null,
              };
            },
          },
        });

        // This cleans up the de-referenced card field from the cache
        cache.gc();
      },
    });
  }

  return {
    create: handleCreate,
    update: handleUpdate,
    remove: handleRemove,
  };
};
